import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTaskStore } from '../../stores/taskStore';
import { useThemeStore } from '../../stores/themeStore';

const TASK_TYPE_LABELS: Record<string, string> = {
  generate_image: '图片生成',
  generate_audio: '音色生成',
  generate_video: '视频生成',
  video_project_character_generation: '资产搭建',
};

const TRACKED_TASK_TYPES = new Set(Object.keys(TASK_TYPE_LABELS));

type TaskProgressIndicatorProps = {
  compact?: boolean;
};

type DropdownPosition = {
  left: number;
  top: number;
};

const TaskProgressIndicator: React.FC<TaskProgressIndicatorProps> = ({ compact = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const activeTasks = useTaskStore((s) =>
    Object.values(s.activeTasks).filter(
      (t) => {
        const status = t.status.toLowerCase();
        return TRACKED_TASK_TYPES.has(t.type) && (status === 'queued' || status === 'running');
      }
    )
  );
  const count = activeTasks.length;

  useLayoutEffect(() => {
    if (!expanded || count === 0) {
      setDropdownPosition(null);
      return;
    }

    const updateDropdownPosition = () => {
      const button = buttonRef.current;
      const panel = panelRef.current;
      if (!button || !panel) return;

      const viewportMargin = 12;
      const triggerRect = button.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const availableWidth = Math.max(0, window.innerWidth - viewportMargin * 2);
      const availableHeight = Math.max(0, window.innerHeight - viewportMargin * 2);
      const panelWidth = Math.min(panelRect.width, availableWidth);
      const panelHeight = Math.min(panelRect.height, availableHeight);
      const maxLeft = Math.max(viewportMargin, window.innerWidth - panelWidth - viewportMargin);
      const maxTop = Math.max(viewportMargin, window.innerHeight - panelHeight - viewportMargin);

      setDropdownPosition({
        left: Math.min(Math.max(triggerRect.left, viewportMargin), maxLeft),
        top: Math.min(Math.max(triggerRect.top - panelHeight - 8, viewportMargin), maxTop),
      });
    };

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    return () => window.removeEventListener('resize', updateDropdownPosition);
  }, [expanded, count]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`flex h-8 items-center rounded-lg border text-xs font-semibold transition-all ${
          compact ? 'w-9 justify-center gap-1 px-0' : 'gap-2 px-2.5'
        } ${
          count > 0
            ? isLight
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-100/70 hover:bg-emerald-100'
              : 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/15'
            : isLight
            ? 'border-stone-200 bg-white/70 text-stone-400 hover:bg-stone-100 hover:text-stone-600'
            : 'border-white/[0.06] bg-white/[0.03] text-stone-500 hover:bg-white/[0.07] hover:text-stone-300'
        }`}
        title={count > 0 ? '查看任务进度' : '当前没有进行中的任务'}
        aria-label={count > 0 ? '查看任务进度' : '当前没有进行中的任务'}
        aria-haspopup="dialog"
        aria-expanded={expanded}
      >
        {count > 0 ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            {compact ? <span className="text-[10px] leading-none">{count}</span> : (
              <>
                <span className="hidden sm:inline">任务</span>
                {count}
              </>
            )}
          </>
        ) : (
          <>
            <span className="h-2 w-2 rounded-sm bg-stone-300 dark:bg-stone-600" />
            {compact ? null : <span className="text-[10px]">任务空闲</span>}
          </>
        )}
      </button>

      {expanded && count > 0 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setExpanded(false)} />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="任务进度"
            className={`fixed z-50 animate-fade-in-up ${!dropdownPosition ? 'pointer-events-none opacity-0' : ''}`}
            style={{
              left: dropdownPosition?.left ?? 0,
              top: dropdownPosition?.top ?? 0,
              width: 'min(18rem, calc(100vw - 1.5rem))',
            }}
          >
            <div
              className={`flex max-h-[min(28rem,calc(100vh-1.5rem))] flex-col overflow-hidden rounded-xl border p-3 shadow-xl ${
                isLight ? 'border-stone-200 bg-white' : 'border-stone-700/50 bg-stone-800'
              }`}
            >
              <h3 className={`mb-2 shrink-0 text-xs font-semibold ${isLight ? 'text-stone-800' : 'text-stone-100'}`}>
                进行中的任务 ({count})
              </h3>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                {activeTasks.map((task) => (
                  <div key={task.taskId} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-medium ${isLight ? 'text-stone-600' : 'text-stone-300'}`}>
                        {TASK_TYPE_LABELS[task.type] || task.type}
                      </span>
                      <span className="font-mono text-[11px] text-emerald-600">
                        {Math.round(task.progress)}%
                      </span>
                    </div>
                    <p className={`truncate text-[10px] ${isLight ? 'text-stone-400' : 'text-stone-500'}`}>
                      {task.phase}
                    </p>
                    <div className={`h-1 w-full overflow-hidden rounded-full ${isLight ? 'bg-stone-100' : 'bg-stone-700'}`}>
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskProgressIndicator;
